<template>
  <nav v-if="items.length > 4" class="text-subtle">
    <ul class="flex items-center gap-4">
      <li v-for="item in items" :key="item.label">
        <nuxt-link
          class="rounded p-2 transition-all hover:text-accent"
          :class="{
            'bg-accent-50 font-semibold text-accent': isActive(item.href),
          }"
          :href="item.href"
          @click="onItemClick"
        >
          {{ item.label }}
        </nuxt-link>
      </li>
    </ul>
  </nav>
</template>

<script lang="ts" setup>
import type { I18nMessage } from "@pollen/core/model/i18n.model";

import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CoursePackageWithDetailsView } from "~/business-areas/course-package/composables/course-packages.hook";

const properties = defineProps<{
  course: CoursePageView;
  packages: CoursePackageWithDetailsView[];
}>();

const { t } = useI18n();
const items = computed<{ label: I18nMessage; href: string }[]>(() => {
  return [
    ...(properties.course.is_masterclass || !properties.course.description
      ? []
      : [
          {
            label: t("course.page.description_navigation_label"),
            href: "#overview",
          },
        ]),
    {
      label: t("course.page.audience_navigation_label"),
      href: "#audience",
    },
    {
      label: t("course.page.outcomes_navigation_label"),
      href: "#outcomes",
    },
    {
      label: t("course.page.teacher_details_navigation_label"),
      href: "#trainer",
    },
    ...(properties.course.ratings && !properties.course.is_masterclass
      ? [
          {
            label: t("course.page.ratings.navigation_label"),
            href: "#ratings",
          },
        ]
      : []),
    ...(properties.course.has_program
      ? [
          {
            label: t("course.page.program_navigation_label"),
            href: "#program",
          },
        ]
      : []),
    ...(properties.packages.length > 0
      ? [
          {
            label: t("course.page.pricing_navigation_label"),
            href: "#packages",
          },
        ]
      : []),
  ];
});

const activeItemHrefUponScroll = ref<string | undefined>();

function isActive(href: string) {
  if (activeItemHrefUponScroll.value)
    return href === activeItemHrefUponScroll.value;

  return useRoute().hash === href;
}

let scrollHandlerIsPaused = false;
async function onItemClick() {
  scrollHandlerIsPaused = true;
  activeItemHrefUponScroll.value = undefined;
  await timer(500);
  scrollHandlerIsPaused = false;
}

if (process.client) {
  const { height } = useWindowSize();

  let scrollThresholdPerItem: { value: number; href: string }[] = [];

  function scrollHandler() {
    if (scrollHandlerIsPaused) return;
    const activeItem = scrollThresholdPerItem.find(
      (i) => i.value <= document.scrollingElement!.scrollTop + height.value,
    );

    if (!activeItem) return;

    activeItemHrefUponScroll.value = activeItem.href;
  }
  onMounted(async () => {
    await timer(500);
    scrollThresholdPerItem = [...items.value]
      .reverse()
      .map((i) => {
        const element = document.querySelector(i.href);

        if (!element) return;

        return {
          href: i.href,
          value: element.getBoundingClientRect().top + 0.3 * height.value,
        };
      })
      .filter(Boolean);

    window.addEventListener("scroll", scrollHandler);
  });

  onUnmounted(() => window.removeEventListener("scroll", scrollHandler));
}
</script>
